import { useTranslation } from 'react-i18next';
/**
 * Shows which values an annex inherits from its master contract (and
 * from the annex template), flagging the empty ones so the user knows
 * up front which tokens will print blank.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from '../ui/badge';
import { AlertTriangle, ArrowDownToLine } from 'lucide-react';
import { API } from './_shared';

const SOURCE_LABEL = {
  master: 'from master',
  template: 'from template',
  annex: 'on this annex',
};

export const InheritancePanel = ({ contractId, templateId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!contractId) return;
    const qs = templateId ? `?template_id=${templateId}` : '';
    axios.get(`${API}/contracts/${contractId}/inheritance${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [contractId, templateId]);

  if (!data) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="annex-inheritance-panel">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <ArrowDownToLine className="h-4 w-4 text-slate-500" />
        {t('auto.ctr.inheritedValues')}
        {data.missing_count > 0 && (
          <Badge variant="secondary" className="ml-auto" data-testid="annex-inheritance-missing">
            <AlertTriangle className="h-3 w-3 mr-1 text-amber-600" />
            {data.missing_count} will print blank
          </Badge>
        )}
      </div>
      <table className="mt-2 w-full text-xs">
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.label} className="border-t border-slate-200/70"
                data-testid={`inheritance-row-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <td className="py-1 pr-2 text-slate-500 whitespace-nowrap">{r.label}</td>
              <td className={`py-1 pr-2 ${r.empty ? 'text-amber-600 italic' : 'text-slate-800'}`}>
                {r.empty ? '— empty' : r.value}
              </td>
              <td className="py-1 pr-2 font-mono text-[10px] text-slate-400 whitespace-nowrap">{r.token}</td>
              <td className="py-1 text-right text-[10px] text-slate-400 whitespace-nowrap">
                {SOURCE_LABEL[r.source] || r.source}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InheritancePanel;
